import { bootstrapApplication } from '@angular/platform-browser';
import { ConfigurationService } from '@processpuzzle/util';
import { KeycloakAuthConfig, resolveTenantRealm } from '@processpuzzle/auth/domain';
import { environment as buildTimeEnv } from './environments/environment';
import { EnvironmentVariables } from './environments/environment-variables';
import { AppComponent } from './app/app.component';
import { createAppConfig } from './app/app.config';
import { RuntimeConfiguration } from './app/runtime-configuration';
import { supportedLocales, tenantFromUrl } from './app/tenant-from-url';

/**
 * The container's rendered environment, or the build-time one when the file is not there.
 *
 * `assets/runtime-env.json` is written by the Docker entrypoint from `envsubst`, which is what lets
 * one image serve every stage. `cache: 'no-store'` because a cached copy would pin the first stage
 * the browser ever saw.
 */
async function loadEnvironment(): Promise<EnvironmentVariables> {
  try {
    const response = await fetch('assets/runtime-env.json', { cache: 'no-store' });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    return (await response.json()) as EnvironmentVariables;
  } catch (error) {
    console.warn('runtime-env.json not available, using build-time environment', error);
    return buildTimeEnv as EnvironmentVariables;
  }
}

/**
 * Substitutes the tenant read from the URL into the configured realm template.
 *
 * One bundle serves every tenant, so the realm is not knowable at build time. It has to be settled
 * *here*, before `bootstrapApplication`: `KeycloakAuthService` constructs its `Keycloak` instance
 * eagerly from `AUTH_SERVICE_CONFIG.realm`, and the app initializer that restores an SSO session
 * runs immediately after. A route resolver would be far too late.
 *
 * `AUTH_SERVICE_CONFIG` is `readonly`, so this returns a new configuration rather than writing
 * through it. When the template names no `{orgKey}` — or the URL names no plausible tenant — the
 * configuration is handed back untouched and the fixed realm applies.
 */
function withTenantRealm(runtimeConfiguration: RuntimeConfiguration, orgKey?: string): RuntimeConfiguration {
  const authenticationConfiguration = runtimeConfiguration.AUTHENTICATION_CONFIGURATION;
  const authServiceConfig = authenticationConfiguration.AUTH_SERVICE_CONFIG as KeycloakAuthConfig | undefined;
  if (!authServiceConfig?.realm?.includes('{orgKey}')) return runtimeConfiguration;

  // `/${orgKey}` rather than the real pathname, so that the realm is resolved from the tenant this
  // application actually recognises. Passing the live path would let `resolveTenantRealm` read
  // `signup` or `welcome` as a tenant again, which is the whole bug `tenantFromUrl` exists to fix —
  // and the realm is the half of it that breaks authentication rather than merely navigation.
  const realm = resolveTenantRealm(authServiceConfig.realm, orgKey ? `/${orgKey}` : '/', authenticationConfiguration.FALLBACK_AUTH_REALM);
  return {
    ...runtimeConfiguration,
    AUTHENTICATION_CONFIGURATION: {
      ...authenticationConfiguration,
      AUTH_SERVICE_CONFIG: { ...authServiceConfig, realm },
    },
  };
}

/**
 * Points authentication at the Keycloak origin this deployment actually runs, when the environment
 * names one.
 *
 * `run-time-conf/config.*.json` is baked into the image, and one image serves CI, local compose and
 * production. Those three share `PIPELINE_STAGE=prod` and therefore one configuration file, but they
 * do not share a Keycloak hostname, so the origin has to come from the environment; the file value
 * remains the fallback for every stage that needs no override.
 *
 * **The value must be Keycloak's own origin, never a path prefix of this one.** Keycloak scopes
 * `KEYCLOAK_SESSION` and `KEYCLOAK_IDENTITY` to `Path=/realms/<realm>/`, so an `/auth` proxy on this
 * origin puts every Keycloak page on a path those cookies do not match. The browser then withholds
 * them and two things fail silently: the login-status iframe reads no `KEYCLOAK_SESSION`, answers
 * `"changed"`, and keycloak-js clears the tokens it obtained seconds earlier — the next API call
 * refreshes with an undefined token, Keycloak logs `REFRESH_TOKEN_ERROR … "Invalid refresh token"`
 * and the request comes back 401 — while `prompt=none` returns `login_required`, so no session
 * survives a reload either. Both were observed on the local stack; neither is visible in a
 * healthcheck.
 *
 * Only `keycloak` configurations are touched.
 */
function withKeycloakUrl(env: EnvironmentVariables, runtimeConfiguration: RuntimeConfiguration): RuntimeConfiguration {
  const keycloakUrl = env.KEYCLOAK_URL?.trim();
  const authenticationConfiguration = runtimeConfiguration.AUTHENTICATION_CONFIGURATION;
  if (!keycloakUrl || authenticationConfiguration.AUTHENTICATION_PROVIDER !== 'keycloak') return runtimeConfiguration;

  const authServiceConfig = authenticationConfiguration.AUTH_SERVICE_CONFIG as KeycloakAuthConfig | undefined;
  return {
    ...runtimeConfiguration,
    AUTHENTICATION_CONFIGURATION: {
      ...authenticationConfiguration,
      AUTHENTICATION_SERVICE_ROOT: keycloakUrl,
      ...(authServiceConfig ? { AUTH_SERVICE_CONFIG: { ...authServiceConfig, authServerUrl: keycloakUrl } } : {}),
    },
  };
}

/**
 * Asks the customer realm for every Organization the account belongs to: `organization:*`.
 *
 * Customer Home lists all of a customer's applications, and its API authorizes `/customers/{orgKey}`
 * on the token's `organization` claim. The client's default `organization` scope cannot serve that:
 * it maps one organization at most, and none at all for a member of several who was never asked to
 * pick one. Requesting the parameterized form replaces the default scope rather than adding to it.
 *
 * Only for the customer realm. A tenant realm of the old model has no `organization` scope, and
 * Keycloak refuses a login that requests a scope the client does not know.
 */
function withCustomerOrganizationScope(runtimeConfiguration: RuntimeConfiguration): RuntimeConfiguration {
  const authenticationConfiguration = runtimeConfiguration.AUTHENTICATION_CONFIGURATION;
  const authServiceConfig = authenticationConfiguration.AUTH_SERVICE_CONFIG as KeycloakAuthConfig | undefined;
  if (authenticationConfiguration.AUTHENTICATION_PROVIDER !== 'keycloak' || !authServiceConfig) return runtimeConfiguration;
  if (authServiceConfig.realm !== authenticationConfiguration.FALLBACK_AUTH_REALM) return runtimeConfiguration;
  return {
    ...runtimeConfiguration,
    AUTHENTICATION_CONFIGURATION: {
      ...authenticationConfiguration,
      AUTH_SERVICE_CONFIG: { ...authServiceConfig, scope: 'organization:*' },
    },
  };
}

async function bootstrap() {
  const env = await loadEnvironment();
  const configurationService = new ConfigurationService<EnvironmentVariables, RuntimeConfiguration>();
  const loadedConfiguration = (await configurationService.init(env)) as RuntimeConfiguration;

  // Read once, and handed both to the realm substitution and to the application as a value.
  // `ORG_ADMIN_ORG_KEY` is injected when a root-provided service is constructed, which is long
  // before any route activates, so it cannot be a route parameter — and the routes themselves are
  // built around this key, so a URL naming a different tenant does not match at all. Switching
  // tenants is a full page load, which is also what stops org B's data being fetched with org A's
  // token.
  //
  // One read for both, rather than `currentOrgKey()` in two places: the realm and the routes have
  // to agree about which tenant this page is, and two independent reads of the same URL are two
  // chances to disagree.
  const orgKey = tenantFromUrl(supportedLocales(loadedConfiguration.LANGUAGE_CONFIGURATION));
  const runtimeConfiguration = withCustomerOrganizationScope(
    withTenantRealm(
      withKeycloakUrl(env, {
        ...loadedConfiguration,
        BASE_CONFIGURATION: {
          ...loadedConfiguration.BASE_CONFIGURATION,
          TESTBED_BASE_URL: env.TESTBED_BASE_URL,
        },
      }),
      orgKey,
    ),
  );

  await bootstrapApplication(AppComponent, createAppConfig(runtimeConfiguration, orgKey));
}

bootstrap().catch((error) => console.error(error));
